// Pressto writing activities aligned to Really Great Reading programs.
// Each card represents a structured writing exercise that reinforces an RGR
// phonics, morphology, or decoding objective.

export type RGRProgram = 'Countdown' | 'Blast Foundations' | 'HD Word' | 'HD Word Plus' | 'Pressto Originals' | 'EL Education' | 'BrainPop' | 'Reading Horizons' | 'Word Generation' | 'Nearpod' | 'Amplify CKLA' | 'Benchmark Advance' | 'Wit & Wisdom' | 'Britannica Learning';
export type RGRSkill = 'Phonemic Awareness' | 'Phonics' | 'Morphology' | 'Decoding' | 'Composition' | 'ELA' | 'Science' | 'Social Studies' | 'History' | 'Civics';
export type RGRGrade = 'K' | '1st' | '2nd' | '3rd' | '4th' | '5th' | '6th' | '7th' | '8th';
export type RGRBrand = 'RGR' | 'Pressto';
export type RGRStatus = 'live' | 'coming-soon';
// Provenance of the writing prompt:
//  - 'verbatim'         : prompt comes word-for-word from the source curriculum
//  - 'pressto-authored' : Pressto wrote the prompt, aligned to the source's topic/unit
export type RGRSource = 'verbatim' | 'pressto-authored';

export interface RGRLesson {
  brand?: RGRBrand;
  program: RGRProgram;
  unit: string;
  skill: RGRSkill;
  grade: RGRGrade;        // primary recommended grade (default display + sort)
  grades?: RGRGrade[];    // OPTIONAL: all grades this lesson works for. If unset,
                          // the lesson is grade-locked to `grade` for filtering.
  gradeBand: string;
  title: string;
  status?: RGRStatus; // defaults to 'coming-soon'
  // When set, clicking the card opens this template URL (joinpressto.com/templates/{id}).
  // The template flow creates a fresh copy of the assignment for the visiting teacher.
  templateUrl?: string;
  isNew?: boolean; // when true, show a "New" badge on the card
  source?: RGRSource; // prompt provenance — drives the "Pressto Original" / "Source prompt" badge
}

export const RGR_LESSONS: RGRLesson[] = [
  // ───── Live RGR-aligned lessons (the catalog launches here) ─────
  {
    program: 'HD Word',
    unit: 'Unit 16 — /oi/ spelled oi and oy',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: 'A toy or game that brings me joy — describing what I love to play with',
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KD8jKLMwXW',
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Abraham Lincoln",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who Was Abraham Lincoln?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/PVG48R3wNv',
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Alexander Hamilton",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who Was Alexander Hamilton?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/qLd4OOG4O5',
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 1960s Folk Music",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "1960s Folk Music: Songs as a Movement",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vXKj7MkJa2',
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Who Was Abraham Lincoln",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Who Was Abraham Lincoln",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/y9XJXa74ek',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Who Was George Washington",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Who Was George Washington",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3P74xoGJY8',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Who Was Nelson Mandela",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Who Was Nelson Mandela",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/a29JrvpJnG',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Who Was Thomas Jefferson",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Who Was Thomas Jefferson",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/pAdJyoajOb',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Who Was Benjamin Franklin",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Who Was Benjamin Franklin",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DYKwGznjmA',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Who Was Harriet Tubman",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Who Was Harriet Tubman",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/eQKw6adjPX',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Who Was Anne Frank",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Who Was Anne Frank",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dqKwEnQJmx',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Who Was Martin Luther King Jr",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Who Was Martin Luther King Jr",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/37z4za3Jdx',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Space",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Space",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/bvpJd6ljd8',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Planets",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Planets",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3YZwZG6Jkb',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Oceans",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Oceans",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dXmJ28MjaR',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/YVXwpBB40O',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystem",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystem",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KeVjNV5jd7',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Hurricane",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Hurricane",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/yGLwvvgwkN',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Volcano",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Volcano",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/EmRjDkn40r',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animals",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animals",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0gNjo6x4YD',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/xMK4956408',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Oceanos",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Oceanos",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/mWKjeZWw6o',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Delaware",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Delaware",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DMOJBXGj8N',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Climate",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Climate",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/051JqdNwKA',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Volcanoes",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Volcanoes",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/ybOJ0kvwzR',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Exploring Dday",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Exploring Dday",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/o8zwV6Yw0N',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Exploring Earth",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Exploring Earth",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/A5DwmX0jOY',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Extreme Weather",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Extreme Weather",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/7oQj3OzjYB',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Harnessing Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Harnessing Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/gVv41nQj0A',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Solar Flare",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Solar Flare",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VY7jQl84D1',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Planetary Plants",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Planetary Plants",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/NqMJRPzja9',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Climate Change",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Climate Change",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KD8jKlpwXW',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Classifications",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Classifications",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p7N4AR6jRe',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Classification",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Classification",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0M5jYGMJl2',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Weather Channels",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Weather Channels",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/BgXJbEzwWD',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Severe Weather",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Severe Weather",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p9RjkzN4ry',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Survival",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Survival",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0Z2jnMA498',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Climate Zones",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Climate Zones",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/PVG48OQJNv',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Weather Impacts",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Weather Impacts",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/qLd4O5bwO5',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Weathering Erosion",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Weathering Erosion",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vXKj78Ywa2',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Climate Controls",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Climate Controls",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/07WwWAR4qv',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems 57",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Ecosystems 57",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjMamjO6',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Desert",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Desert",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24go54N3',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Introduction",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Introduction",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/Zxzwl3Mj9r',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animals Adaptation",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animals Adaptation",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JPxoJ9W',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Spaced Out",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Spaced Out",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LoXJeV',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Grasslands",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Grasslands",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/MWnwa6vJme',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Saltwater",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Saltwater",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vxBJ5k0j5E',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Tundra",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Tundra",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/y9XJXe74ek',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Adventure",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Adventure",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3P74x1GjY8',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Solar Eclipse",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Solar Eclipse",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/a29JrdpjnG',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Planet Mercury",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Planet Mercury",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/pAdJyLawOb',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Freshwater",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Freshwater",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DYKwGOnwmA',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Barnyard Animals",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Barnyard Animals",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/eQKw6ndwPX',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Outer Space",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Outer Space",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dqKwEXQjmx',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Songs",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Songs",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/37z4zd3wdx',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Weather Instruments",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Weather Instruments",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/bvpJdrljd8',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Energy Transfer",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Energy Transfer",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3YZwZ26wkb',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 World Ecosystems35",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "World Ecosystems35",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dXmJ2gM4aR',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Energy Balance",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Energy Balance",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/YVXwpnB40O',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Weather Instruments35",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Weather Instruments35",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KeVjN554d7',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Energy Transfer35",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Energy Transfer35",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/yGLwvmgjkN',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Los Planetas",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Los Planetas",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/EmRjDRnJ0r',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Os Planetas",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Os Planetas",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0gNjokxjYD',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Clasificacin Animal",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Clasificacin Animal",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/xMK4916408',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Classificao Animal",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Classificao Animal",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/mWKje3WJ6o',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Sobrevivncia Animal",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Sobrevivncia Animal",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DMOJBLGj8N',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Sistema Solar",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Sistema Solar",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/051Jq8NwKA',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Westward Expansion",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Westward Expansion",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/ybOJ0Ev4zR',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Energy Renewable",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Energy Renewable",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/o8zwVEYj0N',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Caves",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Caves",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/A5Dwmz0JOY',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Presidents Day",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Presidents Day",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/7oQj3gzjYB',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Constitution Day",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Constitution Day",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/gVv41LQJ0A',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 47 Presidents",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "47 Presidents",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VY7jQr8wD1',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Ancient Egyptian Hieroglyphs",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ancient Egyptian Hieroglyphs",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/NqMJRrzJa9',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Predators and Prey",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Predators and Prey",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KD8jKgpjXW',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Exploring Pearl Harbor",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Exploring Pearl Harbor",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p7N4Ay64Re',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Exploring Black Tuesday",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Exploring Black Tuesday",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0M5jYgMJl2',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Deep Ocean Feeding",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Deep Ocean Feeding",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/BgXJbdzjWD',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 The Us Constitution",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "The Us Constitution",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p9RjklNwry',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Empires Ancient Egypt",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Empires Ancient Egypt",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0Z2jnbAj98',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Speed and Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Speed and Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/PVG485QjNv',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Gas Giants Weather",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Gas Giants Weather",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/qLd4OxbjO5',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Classification Identification",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Classification Identification",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vXKj7gYja2',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 American Revolution Battles",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "American Revolution Battles",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/07WwWaR4qv',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 American Revolution Tea",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "American Revolution Tea",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjMrmwO6',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Identifying Continents Oceans",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Identifying Continents Oceans",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24g354N3',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Diagram Energy Force",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Diagram Energy Force",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/ZxzwlEMJ9r',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Forms of Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Forms of Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JPpoJ9W',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 The Animal Kingdom",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "The Animal Kingdom",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LlX4eV',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Weather vs Climate",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Weather vs Climate",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/BgXJbdDjWD',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Interdependence Energy Food",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Interdependence Energy Food",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p9RjklXwry',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Interdependence Animals Seasons",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Interdependence Animals Seasons",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0Z2jnbvj98',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 What Is Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "What Is Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/PVG485BjNv',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Westward Expansion Timeline",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Westward Expansion Timeline",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/qLd4Ox9jO5',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Identifying Climate Types",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Identifying Climate Types",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vXKj7gAja2',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Solar System Exploration",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Solar System Exploration",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/07WwWaG4qv',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Solar System Defined",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Solar System Defined",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjMrpwO6',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Work Force Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Work Force Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24gvdJN3',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Habitat of Animals",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Habitat of Animals",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/Zxzwlvy49r',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Earths Rotation Revolution",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Earths Rotation Revolution",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JP0rJ9W',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Weathering and Erosion",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Weathering and Erosion",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LOkJeV',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 How Volcanoes Work",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "How Volcanoes Work",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/MWnwa324me',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Tropical Forest",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Tropical Forest",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vxBJ5Lkw5E',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 American Revolution Dates",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "American Revolution Dates",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/y9XJXdMJek',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 The Solar System",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "The Solar System",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3P74xdd4Y8',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 All About Animals",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "All About Animals",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/a29JrL3jnG',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Dinosaurs or Animals",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Dinosaurs or Animals",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/pAdJyz94Ob',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Learn About Animals",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Learn About Animals",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DYKwGWMwmA',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Wild Animal Songs",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Wild Animal Songs",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/eQKw6LN4PX',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Professions Farm Animals",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Professions Farm Animals",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dqKwEOd4mx',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Weather Related Hazards",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Weather Related Hazards",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/37z4z2G4dx',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Survival Application",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Survival Application",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/bvpJdYp4d8',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Energy Patterns Examples",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Energy Patterns Examples",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3YZwZlO4kb',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Physical Weathering Examples",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Physical Weathering Examples",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dXmJ2EGwaR',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Conserving Energy Examples",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Conserving Energy Examples",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/YVXwp0Gw0O',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Sense Examples",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Animal Sense Examples",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KeVjNWAwd7',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Climate Zones 58",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Climate Zones 58",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/yGLwvgnJkN',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 The Suns Energy",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "The Suns Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/EmRjD81j0r',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Dwight Howard Superman",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Dwight Howard Superman",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0gNjovL4YD',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 The Civil War",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "The Civil War",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/xMK49XLJ08',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Nuestro Sistema Solar",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Nuestro Sistema Solar",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/mWKjev6w6o',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 El Reino Animal",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "El Reino Animal",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DMOJBkq48N',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Nosso Sistema Solar",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Nosso Sistema Solar",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/051Jqv74KA',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 O Reino Animal",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "O Reino Animal",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/ybOJ0Y34zR',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 La Supervivencia Animal",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "La Supervivencia Animal",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/o8zwVVWw0N',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Our Solar System",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Our Solar System",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/A5DwmvnJOY',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 William Howard Taft",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "William Howard Taft",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/7oQj36E4YB',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Warren G Harding",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Warren G Harding",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/gVv412Nj0A',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animals With Superpowers",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animals With Superpowers",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VY7jQE1jD1',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Super Intelligent Animals",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Super Intelligent Animals",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/NqMJRY2wa9',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Andys Animal Sounds",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Andys Animal Sounds",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KD8jK8KwXW',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Andys Space Song",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Andys Space Song",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p7N4AbXjRe',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Group Songs",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Group Songs",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0M5jY2vwl2',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Animal Classifications Part",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Animal Classifications Part",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/BgXJb3D4WD',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Rotation and Revolution",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Rotation and Revolution",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p9RjkvX4ry',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems Tropical Forest35",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Ecosystems Tropical Forest35",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0Z2jn9v498',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Classifying Predators Prey",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Classifying Predators Prey",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/PVG481BjNv',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Sustainability Alternative Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Sustainability Alternative Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/qLd4O394O5',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 National Space Day",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "National Space Day",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vXKj7aAJa2',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Mummification in Ancient Egypt",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Mummification in Ancient Egypt",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/07WwWdGJqv',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Ecosystems an Active Volcano",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Ecosystems an Active Volcano",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjM7pJO6',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 How Predators Capture Prey",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "How Predators Capture Prey",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24g7d4N3',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Exploring the Magna Carta",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Exploring the Magna Carta",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/Zxzwloyj9r',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Exploring the Protestant Reformation",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Exploring the Protestant Reformation",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JP5r49W',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Exploring the French Revolution",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Exploring the French Revolution",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LPk4eV',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 Exploring the Columbian Exchange",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Exploring the Columbian Exchange",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/MWnwaY2wme',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 The Solar System Mars",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "The Solar System Mars",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vxBJ5Pkw5E',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Life on Other Planets",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Life on Other Planets",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/y9XJXZMjek',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Identifying Forces of Energy",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Identifying Forces of Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3P74xPd4Y8',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 American Revolution the Causes",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "American Revolution the Causes",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/a29Jr334nG',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 Diagram Forms of Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Diagram Forms of Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/pAdJyA9JOb',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Social Studies \u00b7 The Civil Rights Movement",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "The Civil Rights Movement",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DYKwG7MwmA',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Nearpod',
    unit: "Science \u00b7 All Sorts of Energy",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "All Sorts of Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/eQKw61NjPX',
    grades: ["3rd", "4th", "5th"],
    source: 'pressto-authored',
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.01",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "What is fair?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0Z2jnxQJ98',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.02",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Should students share responsibility for each other's behavior in school?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/PVG48X34Nv',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.03",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who should decide what we eat?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/qLd4OYGwO5',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.04",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Should students be required to wear uniforms?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vXKj71kJa2',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.05",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Should everyone learn a second language?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/07WwWWbwqv',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.06",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who cares where we came from?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjMVRwO6',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.07",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who gets to decide what's safe?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24g6OJN3',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.08",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Should wild animals be adopted as pets?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/Zxzwlzg49r',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.09",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "When is it acceptable to break the rules?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JP6qj9W',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.10",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Why do we wear what we wear?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LQyjeV',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.11",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Why do we buy what we buy?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/BgXJbpzjWD',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 4.12",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Why do we value what we value?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p9RjkENJry',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.01",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Where do I belong?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0Z2jnxAJ98',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.02",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Should everyone be included?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/PVG48XQ4Nv',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.03",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Why should I care?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/qLd4OYbwO5',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.04",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "What divides us and how can we resolve our differences?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vXKj71YJa2',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.05",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Why do we fight?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/07WwWWRwqv',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.06",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Do we need to give up our privacy to protect our communities?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjMVmwO6',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.07",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "The Power of Power",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24g65JN3',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.08",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Why do communities have different ideas about what brings happiness?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/ZxzwlzM49r',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.09",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "How can one person influence a community?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JP6oj9W',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.10",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Do we need laws to regulate our behavior?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LQXjeV',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.11",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "What kinds of protections justify restricting our freedoms?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/MWnwa2vJme',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'Word Generation',
    unit: "Word Generation \u00b7 Unit 5.12",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Can we create a better society?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vxBJ5W0j5E',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 George Washington",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who Was George Washington?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/07WwWEb4qv',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Thomas Jefferson",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who Was Thomas Jefferson?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjMnRwO6',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Benjamin Franklin",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who Was Benjamin Franklin?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24gpOjN3',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Frederick Douglass",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Who Was Frederick Douglass?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/ZxzwlNg49r',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Harriet Tubman",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who Was Harriet Tubman?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JPOqw9W',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Martin Luther King Jr.",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who Was Martin Luther King Jr.?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LkyJeV',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Rosa Parks",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Who Was Rosa Parks?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/MWnwa5Ewme',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Anne Frank",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Who Was Anne Frank?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vxBJ5pYw5E',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 The Civil War",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Explaining the American Civil War",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/y9XJX534ek',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 The American Revolution",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Explaining the American Revolution",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3P74xbDwY8',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 The Declaration of Independence",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "What the Declaration of Independence Says",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/a29Jr0MjnG',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 The Constitution",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Why the Constitution Still Matters",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/pAdJyk8jOb',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 The Underground Railroad",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Explaining the Underground Railroad",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DYKwGrGjmA',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 Women's Suffrage",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "How Women Won the Right to Vote",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/eQKw6WxJPX',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "US History \u00b7 World War II",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Explaining World War II",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dqKwE1Lwmx',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "World History \u00b7 Ancient Egypt",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Describing Ancient Egypt",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/37z4znz4dx',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "World History \u00b7 Ancient Rome",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Describing Ancient Rome",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/bvpJdvxjd8',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "World History \u00b7 Ancient Greece",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Describing Ancient Greece",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3YZwZ6Dwkb',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "World History \u00b7 The Renaissance",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Describing the Renaissance",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dXmJ2nrJaR',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Photosynthesis",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "How Plants Make Food: Photosynthesis",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/YVXwpA6J0O',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Cells",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Describing Cells",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KeVjNQq4d7',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Ecosystems",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Describing an Ecosystem",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/yGLwv2rwkN',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Food Chains",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "How Food Chains Work",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/EmRjDz6J0r',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Adaptation",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "How Animals Adapt to Survive",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0gNjoAnJYD',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Plant Life Cycle",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "The Life Cycle of a Plant",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/xMK4979w08',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 The Water Cycle",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "How the Water Cycle Works",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/mWKjeE9j6o',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Weather",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Explaining the Weather",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DMOJB1Rw8N',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Climate Change",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Why Climate Change Matters",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/051JqyzJKA',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Volcanoes",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "What Causes a Volcano to Erupt?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/ybOJ0m74zR',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Matter",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "States of Matter",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/o8zwVXP40N',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Forces and Motion",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "How Forces Cause Motion",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/A5DwmxxjOY',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Energy",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Forms of Energy",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/7oQj31OJYB',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Science \u00b7 Simple Machines",
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Describing Simple Machines",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/gVv41qL40A',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "English \u00b7 Edgar Allan Poe",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Who Was Edgar Allan Poe?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VY7jQVEwD1',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "English \u00b7 Shakespeare",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Who Was William Shakespeare?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/NqMJRxZja9',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "English \u00b7 Greek Mythology",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Describing Greek Mythology",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KD8jK3QjXW',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Arts \u00b7 Jazz",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "What Is Jazz?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p7N4AGYwRe',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Arts \u00b7 Pablo Picasso",
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Who Was Pablo Picasso?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0M5jY57jl2',
    source: 'pressto-authored',
    grades: ['4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Health \u00b7 Nutrition",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Why Good Nutrition Matters",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/BgXJbpWjWD',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'BrainPop',
    unit: "Health \u00b7 Mindfulness",
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "What Is Mindfulness?",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p9RjkELJry',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 3, Module 1 · Unit 2',
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Overcoming a Challenge in Accessing Books in Mongolia",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KeVjNLqJd7',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 3, Module 2 · Unit 1',
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "A Pourquoi Tale About a Frog",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/yGLwv8r4kN',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 3, Module 2 · Unit 2',
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "How Poison Dart Frogs Survive",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/EmRjDQ6w0r',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 3, Module 2 · Unit 3',
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Revising My Freaky Frog Informational Essay",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0gNjopnwYD',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 3, Module 3 · Unit 2',
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Revising My Peter Pan Book Review",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/xMK49r9j08',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 3, Module 4 · Unit 2',
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "Why We Should Care About Water Conservation",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/mWKjex946o',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 3, Module 4 · Unit 3',
    skill: 'Composition',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: "PSA Script: A Water Issue I Care About",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DMOJB7R48N',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 4, Module 1 · Unit 2',
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Revising My Literary Essay About a Poet",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/051JqAzJKA',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 4, Module 2 · Unit 2',
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "How the Pufferfish Defends Itself",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/ybOJ0b7jzR',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 4, Module 2 · Unit 3',
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Choose-Your-Own-Adventure: An Animal Defense Story",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/o8zwVPP40N',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 4, Module 3 · Unit 2',
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "Robert's Goodbye: A First-Person Scene from Divided Loyalties",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/A5DwmpxjOY',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 4, Module 3 · Unit 3',
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "A Loyalist Broadside on the American Revolution",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/7oQj3ZO4YB',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 4, Module 4 · Unit 2',
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "A Theme in The Hope Chest",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/gVv41kLw0A',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 4, Module 4 · Unit 3',
    skill: 'Composition',
    grade: '4th',
    gradeBand: '4th Grade',
    title: "PSA Script: Why Kids Should Take Action",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VY7jQbEwD1',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 5, Module 1 · Unit 2',
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Revising My Esperanza Rising Compare/Contrast Essay",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/NqMJRkZ4a9',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 5, Module 2 · Unit 2',
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "How Sensory Language Brings the Rainforest to Life",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/KD8jKbQ4XW',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 5, Module 3 · Unit 1',
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Summarizing Promises to Keep, Pages 60\u201361",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p7N4AQY4Re',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 5, Module 3 · Unit 2',
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "The Most Important Factor in Jackie Robinson's Success",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0M5jYA7jl2',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 5, Module 4 · Unit 1',
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "PSA Script: Staying Safe in a Natural Disaster",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/BgXJbZWJWD',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 5, Module 4 · Unit 2',
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Point of View in 'Job' by Kwame Dawes",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p9Rjk0Lwry',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'EL Education',
    unit: 'Grade 5, Module 4 · Unit 3',
    skill: 'Composition',
    grade: '5th',
    gradeBand: '5th Grade',
    title: "Two Personal Items for My Family's Emergency Preparedness Kit",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0Z2jn0QJ98',
    source: 'verbatim',
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 1 — Closed Syllables',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "A time I stayed calm under pressure",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjM57jO6',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 2 — Closed Syllables with 2-Sound Blends and Digraph Blends',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "A challenge I worked through",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24gb7JN3',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 3 — Closed Syllables with Trigraphs and 3-Sound Blends',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "Something I built or made",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/Zxzwl7Pj9r',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 4 — 2- and 3-Syllable Closed Syllable Words',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "Describing a routine I follow",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JPrXj9W',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 5 — Open Syllables',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "Describing my hero",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LL84eV',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 6 — Schwa',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "Describing an everyday object \u2014 what makes it useful",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/BgXJbMWwWD',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 7 — VCE Words (Magic E)',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "A time I made something special",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/p9Rjk3Ljry',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 8 — 2-Syllable VCE with Schwa',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "Describing a place I'd love to visit",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/0Z2jn6Qj98',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 9 — Most Common Vowel Teams (ai, ay, ee, ea, igh, ow, oa)',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "A day at the beach, lake, or pool",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/PVG48k3JNv',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 10 — Less Common Vowel Teams (ie, ey)',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "Describing the perfect day",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/qLd4OMGwO5',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 11 — R-Controlled /or/ and /ar/',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "My favorite sport or game",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vXKj7bk4a2',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 12 — /or/ spelled or, our, ore, oor, oar',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd – 5th Grade',
    title: "Describing a story I love",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/07WwWQb4qv',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 13 — /er/ spelled er, ir, ur, ear',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing a person I admire",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/VPgjM5RjO6',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 14 — Cumulative R-Controlled Review',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "A road trip or adventure",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/av24gbOJN3',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 15 — /oo/ spelled oo, u, u-e, ew',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing a meal that means something to me",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/Zxzwl7gj9r',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 17 — /ou/ spelled ou and ow',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "A time I felt proud",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/MWnwaWE4me',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 18 — Short /oo/ spelled oo and u',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing a book that hooked me",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/vxBJ5ZY45E',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 19 — /aw/ spelled au and aw',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing something awesome I saw",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/y9XJXr3jek',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 20 — Cumulative Vowel Review',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "A time everything came together",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3P74xzDwY8',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 21 — Chunks (-ang, -ing, -ong, -ung, -ank, -ink, -onk, -unk)',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing the weirdest dream I remember",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/a29Jr6MJnG',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 22 — Consonant -le (Castle, Apple, Bubble)',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing my favorite snack",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/pAdJyd8wOb',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 23 — Latin Chunks (-tion, -sion, -ture)',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing an invention that changed the world",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/DYKwG3GwmA',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 24 — Hard and Soft c and g',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing a city I'd love to live in",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/eQKw6rxwPX',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 25 — Consonant Suffixes (-s, -less, -ness, -ment, -ful, -ly)',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing what kindness looks like at school",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dqKwEzLJmx',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 26 — Vowel Suffixes (-es, -ing, -er, -est, -ous, -y, -able, -ible)',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing the smartest person I know",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/37z4zMzJdx',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 27 — 1-1-1 Doubling Rule and the 3 Sounds of -ed',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "A time I really pushed myself",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/bvpJdlxJd8',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 28 — Prefixes (dis-, con-, un-, im-, in-)',
    skill: 'Phonics',
    grade: '4th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing something that's misunderstood",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/3YZwZoDJkb',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 29 — Prefixes (re-, pre-, pro-)',
    skill: 'Phonics',
    grade: '5th',
    gradeBand: '3rd – 5th Grade',
    title: "Something we should do (or stop doing)",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/dXmJ2RrJaR',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 30 — Cumulative Review of Suffixes and Prefixes',
    skill: 'Phonics',
    grade: '5th',
    gradeBand: '3rd – 5th Grade',
    title: "A change my community needs",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/YVXwpE6J0O',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 31 — Closed Syllable Exceptions (ost, old, ild, ind, olt)',
    skill: 'Phonics',
    grade: '5th',
    gradeBand: '3rd – 5th Grade',
    title: "A time I had to be bold",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/nX8JPrqj9W',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'HD Word Plus',
    unit: 'Unit 32 — Split Vowels',
    skill: 'Phonics',
    grade: '5th',
    gradeBand: '3rd – 5th Grade',
    title: "Describing how something I love actually works",
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/kyB4LLy4eV',
    source: 'pressto-authored',
    grades: ['3rd', '4th', '5th'],
    isNew: true,
  },
  {
    program: 'Blast Foundations',
    unit: 'Unit 5 — The Rush to Camp',
    skill: 'Phonics',
    grade: '1st',
    gradeBand: '1st Grade',
    title: 'Personal narrative with short-u words and Heart Words: could, when, take, gave',
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/eQKw6MXjPX',
    source: 'pressto-authored',
  },
  {
    program: 'HD Word',
    unit: 'Unit 25 — Fearless',
    skill: 'Phonics',
    grade: '3rd',
    gradeBand: '3rd Grade',
    title: 'Personal narrative about being brave, using consonant suffixes -less, -ness, -ful, -ly',
    status: 'live',
    templateUrl: 'https://joinpressto.com/templates/YVXwpyOJ0O',
    source: 'pressto-authored',
  },

  // Countdown (K) — real unit numbers from RGR's 2018 Countdown S&S (28 units across 3 books)
  { program: 'Countdown', unit: 'Unit 2 — Sound Stories', skill: 'Phonemic Awareness', grade: 'K', gradeBand: 'Kindergarten', title: 'Identifying initial sounds in spoken words' },
  { program: 'Countdown', unit: 'Unit 6 — Letter Cluster 1: m, t, p, a', skill: 'Phonics', grade: 'K', gradeBand: 'Kindergarten', title: 'Connecting letters to short-vowel sounds' },
  { program: 'Countdown', unit: 'Unit 18 — Closed Syllables', skill: 'Phonics', grade: 'K', gradeBand: 'Kindergarten', title: 'Building CVC words with picture support' },

  // Blast Foundations (1st) — real unit numbers from RGR's 2014 Blast S&S (25 units)
  { program: 'Blast Foundations', unit: 'Unit 2 — Closed Syllables', skill: 'Decoding', grade: '1st', gradeBand: '1st Grade', title: 'Reading and writing closed syllables' },
  { program: 'Blast Foundations', unit: 'Unit 8 — Digraphs ch & wh', skill: 'Phonics', grade: '1st', gradeBand: '1st Grade', title: 'Consonant digraphs: sh, ch, th, wh' },
  { program: 'Blast Foundations', unit: 'Unit 10 — Trigraphs tch & dge', skill: 'Phonics', grade: '1st', gradeBand: '1st Grade', title: 'Trigraphs: tch and dge' },
  { program: 'Blast Foundations', unit: 'Unit 11 — Chunk all & Double Trouble Rule', skill: 'Phonics', grade: '1st', gradeBand: '1st Grade', title: 'Welded sounds with the chunk -all' },
  { program: 'Blast Foundations', unit: 'Unit 13 — Two-Sound Blends', skill: 'Phonics', grade: '1st', gradeBand: '1st Grade', title: 'Initial and final two-sound blends' },
  { program: 'Blast Foundations', unit: 'Unit 19 — Vowel-Consonant-e Spellings', skill: 'Phonics', grade: '1st', gradeBand: '1st Grade', title: 'Magic e: writing with VCe words' },

  // HD Word (2nd–3rd) — real unit numbers from RGR's 2015 HD Word S&S (33 units across 3 books)
  { program: 'HD Word', unit: 'Unit 5 — Open Syllables', skill: 'Decoding', grade: '2nd', gradeBand: '2nd Grade', title: 'Open and closed syllables in two-syllable words' },
  { program: 'HD Word', unit: 'Unit 9 — Most Common Vowel Team Spellings', skill: 'Phonics', grade: '2nd', gradeBand: '2nd Grade', title: 'Long-a vowel teams: ai, ay' },
  { program: 'HD Word', unit: 'Unit 9 — Most Common Vowel Team Spellings', skill: 'Phonics', grade: '2nd', gradeBand: '2nd Grade', title: 'Long-e vowel teams: ee, ea' },
  { program: 'HD Word', unit: 'Unit 10 — Less Common Vowel Team Spellings', skill: 'Phonics', grade: '2nd', gradeBand: '2nd Grade', title: 'Less common vowel teams: ie, ey' },
  { program: 'HD Word', unit: 'Unit 11 — R-Controlled /or/ and /ar/', skill: 'Phonics', grade: '2nd', gradeBand: '2nd Grade', title: 'R-controlled vowels: or and ar' },
  { program: 'HD Word', unit: 'Unit 22 — Consonant -le', skill: 'Decoding', grade: '3rd', gradeBand: '3rd Grade', title: 'Reading multisyllabic words with consonant-le' },
  { program: 'HD Word', unit: 'Unit 24 — Hard and Soft c and g', skill: 'Phonics', grade: '3rd', gradeBand: '3rd Grade', title: 'Hard and soft c and g' },
  { program: 'HD Word', unit: 'Unit 26 — Vowel Suffixes', skill: 'Morphology', grade: '3rd', gradeBand: '3rd Grade', title: 'Vowel suffixes: -ing, -ed, -er, -est' },
  { program: 'HD Word', unit: 'Unit 28 — Prefixes (dis-, con-, un-, im-, in-)', skill: 'Morphology', grade: '3rd', gradeBand: '3rd Grade', title: 'Reading and writing with common prefixes' },


];

export const RGR_PROGRAMS: RGRProgram[] = ['Countdown', 'Blast Foundations', 'HD Word', 'HD Word Plus', 'EL Education', 'BrainPop', 'Reading Horizons', 'Word Generation', 'Nearpod', 'Amplify CKLA', 'Benchmark Advance', 'Wit & Wisdom', 'Britannica Learning'];
export const RGR_SKILLS: RGRSkill[] = ['Phonemic Awareness', 'Phonics', 'Morphology', 'Decoding', 'Composition', 'ELA', 'Science', 'Social Studies', 'History', 'Civics'];
export const RGR_GRADES: RGRGrade[] = ['K', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'];
